import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import TravelExploreOutlinedIcon from "@mui/icons-material/TravelExploreOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import ImageSearchOutlinedIcon from "@mui/icons-material/ImageSearchOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import DnsOutlinedIcon from "@mui/icons-material/DnsOutlined";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";

import "./resources.scss";

const articlePlaceholders = [
  {
    id: "article-1",
    category: "OSINT Fundamentals",
    title: "Applying the Intelligence Cycle to Open-Source Research",
    description:
      "A practical guide to structuring collection, evaluation, analysis, and reporting for operational decision-making.",
  },
  {
    id: "article-2",
    category: "Protective Intelligence",
    title: "From Online Discussion to Protective Intelligence Value",
    description:
      "How analysts can separate routine online commentary from information that may warrant closer review.",
  },
  {
    id: "article-3",
    category: "Analyst Tradecraft",
    title: "Documenting Sources and Preserving Analytical Integrity",
    description:
      "A professional approach to source documentation, confidence language, and reproducible research.",
  },
];

const resourceSections = [
  {
    id: "directories",
    title: "Resource Directories",
    description:
      "Broad starting points for locating specialized open-source research tools.",
    icon: TravelExploreOutlinedIcon,
    resources: [
      {
        name: "OSINT Framework",
        url: "https://osintframework.com/",
        domain: "osintframework.com",
        access: "Free resources",
        description:
          "A categorized directory covering usernames, email addresses, domains, social platforms, geolocation, public records, and other investigative areas.",
      },
      {
        name: "Bellingcat Online Investigation Toolkit",
        url: "https://bellingcat.gitbook.io/toolkit",
        domain: "bellingcat.gitbook.io",
        access: "Mostly free",
        description:
          "A maintained collection of mapping, satellite, archiving, media-verification, transportation, and social-research tools.",
      },
    ],
  },
  {
    id: "search-archiving",
    title: "Search and Archiving",
    description:
      "Search refinement, trend discovery, and preservation of public web content.",
    icon: SearchOutlinedIcon,
    resources: [
      {
        name: "Google Advanced Search",
        url: "https://www.google.com/advanced_search",
        domain: "google.com",
        access: "Free",
        description:
          "Refine searches by exact wording, exclusions, language, region, file type, date, domain, and other criteria.",
      },
      {
        name: "Internet Archive Wayback Machine",
        url: "https://web.archive.org/",
        domain: "web.archive.org",
        access: "Free",
        description:
          "Review archived versions of websites and preserve public pages for later reference.",
      },
      {
        name: "Google Trends",
        url: "https://trends.google.com/",
        domain: "trends.google.com",
        access: "Free",
        description:
          "Compare relative search interest across time, geography, and related topics.",
      },
    ],
  },
  {
    id: "media",
    title: "Image and Media Verification",
    description:
      "Reverse-image searching and visual-content verification resources.",
    icon: ImageSearchOutlinedIcon,
    resources: [
      {
        name: "Google Lens",
        url: "https://lens.google.com/",
        domain: "lens.google.com",
        access: "Free",
        description:
          "Search visual matches, identify objects and locations, and locate related versions of an image.",
      },
      {
        name: "TinEye",
        url: "https://tineye.com/",
        domain: "tineye.com",
        access: "Free search",
        description:
          "Reverse-image search for locating prior appearances, alternate versions, and possible sources.",
      },
      {
        name: "InVID Verification Plugin",
        url: "https://www.invid-project.eu/tools-and-services/invid-verification-plugin/",
        domain: "invid-project.eu",
        access: "Free",
        description:
          "Browser-based tools for video keyframes, image analysis, metadata review, and verification workflows.",
      },
    ],
  },
  {
    id: "maps",
    title: "Maps and Geolocation",
    description:
      "Mapping, imagery, street-level context, and environmental reference tools.",
    icon: PublicOutlinedIcon,
    resources: [
      {
        name: "Google Earth",
        url: "https://earth.google.com/",
        domain: "earth.google.com",
        access: "Free",
        description:
          "Satellite imagery, terrain, historical imagery, 3D context, and geographic exploration.",
      },
      {
        name: "OpenStreetMap",
        url: "https://www.openstreetmap.org/",
        domain: "openstreetmap.org",
        access: "Free",
        description:
          "Community-maintained global mapping data useful for roads, buildings, points of interest, and local context.",
      },
      {
        name: "Mapillary",
        url: "https://www.mapillary.com/",
        domain: "mapillary.com",
        access: "Free access available",
        description:
          "Crowdsourced street-level imagery that may supplement conventional mapping services.",
      },
      {
        name: "SunCalc",
        url: "https://www.suncalc.org/",
        domain: "suncalc.org",
        access: "Free",
        description:
          "Estimate sun position, shadows, sunrise, and sunset for location and time-based visual analysis.",
      },
    ],
  },
  {
    id: "infrastructure",
    title: "Domains and Digital Infrastructure",
    description:
      "Public information concerning domains, certificates, hosts, and internet-connected infrastructure.",
    icon: DnsOutlinedIcon,
    resources: [
      {
        name: "ICANN Lookup",
        url: "https://lookup.icann.org/",
        domain: "lookup.icann.org",
        access: "Free",
        description:
          "Review publicly available registration and registrar information for internet domains.",
      },
      {
        name: "Shodan",
        url: "https://www.shodan.io/",
        domain: "shodan.io",
        access: "Free tier",
        description:
          "Search publicly indexed information about internet-connected systems and exposed services.",
      },
      {
        name: "Censys Search",
        url: "https://search.censys.io/",
        domain: "search.censys.io",
        access: "Free tier",
        description:
          "Explore publicly indexed hosts, services, certificates, and internet infrastructure.",
      },
      {
        name: "VirusTotal",
        url: "https://www.virustotal.com/",
        domain: "virustotal.com",
        access: "Free access available",
        description:
          "Review community and security-vendor information associated with files, URLs, domains, and IP addresses.",
      },
    ],
  },
  {
    id: "exposure",
    title: "Exposure Awareness",
    description:
      "Resources for identifying publicly reported account or credential exposure.",
    icon: SecurityOutlinedIcon,
    resources: [
      {
        name: "Have I Been Pwned",
        url: "https://haveibeenpwned.com/",
        domain: "haveibeenpwned.com",
        access: "Free",
        description:
          "Check whether an email address appears in publicly reported breach data maintained by the service.",
      },
    ],
  },
  {
    id: "methodology",
    title: "Methodology and Training",
    description:
      "Guides and educational material for structured, ethical open-source research.",
    icon: SchoolOutlinedIcon,
    resources: [
      {
        name: "Bellingcat Resources",
        url: "https://www.bellingcat.com/category/resources/",
        domain: "bellingcat.com",
        access: "Free",
        description:
          "Articles, methods, case studies, and practical guidance concerning open-source investigations.",
      },
      {
        name: "GIJN Resource Center",
        url: "https://gijn.org/resource/",
        domain: "gijn.org",
        access: "Free",
        description:
          "Research guides, investigative methods, data resources, and training material for professional researchers and journalists.",
      },
      {
        name: "Verification Handbook",
        url: "https://verificationhandbook.com/",
        domain: "verificationhandbook.com",
        access: "Free",
        description:
          "Guidance for verifying digital content, user-generated media, and information during fast-moving events.",
      },
    ],
  },
];

export const Resources = () => {
  const navigate = useNavigate();
  const [externalResource, setExternalResource] = useState(null);
  const [comingSoonArticle, setComingSoonArticle] = useState(null);

  const resourceCount = useMemo(
    () =>
      resourceSections.reduce(
        (total, section) => total + section.resources.length,
        0
      ),
    []
  );

  const closeModals = () => {
    setExternalResource(null);
    setComingSoonArticle(null);
  };

  const continueToExternalResource = () => {
    if (!externalResource?.url) return;

    window.open(
      externalResource.url,
      "_blank",
      "noopener,noreferrer"
    );

    setExternalResource(null);
  };

  return (
    <main className="resources-page">
      <div className="resources-contour-background" aria-hidden="true" />

      <div className="resources-shell">
        <section className="resources-hero">
          <div className="resources-hero-copy">
            <span className="resources-eyebrow">Analyst Reference Library</span>
            <h1>Resources</h1>
            <p>
              A curated starting point for publicly available research tools,
              investigative references, and analyst tradecraft resources.
            </p>

            <div className="resources-hero-note">
              <GavelOutlinedIcon aria-hidden="true" />
              <span>
                External resources are provided for general reference and are
                not owned, operated, sponsored, certified, or endorsed by
                Weathervane Intel.
              </span>
            </div>
          </div>

          <div className="resources-hero-summary">
            <div className="resources-hero-summary-header">
              <MenuBookOutlinedIcon aria-hidden="true" />
              <span>Reference Library</span>
            </div>

            <div className="resources-hero-summary-grid">
              <div>
                <span>Resource categories</span>
                <strong>{resourceSections.length}</strong>
              </div>

              <div>
                <span>External resources</span>
                <strong>{resourceCount}</strong>
              </div>
            </div>

            <button
              type="button"
              className="resources-return-button"
              onClick={() => navigate("/")}
            >
              Return to Intelligence Center
            </button>
          </div>
        </section>

        <section
          className="resources-articles-section"
          aria-labelledby="weathervane-articles-heading"
        >
          <div className="resources-section-heading">
            <div>
              <span className="resources-section-kicker">
                Weathervane Intel
              </span>
              <h2 id="weathervane-articles-heading">
                Analyst Articles and Guides
              </h2>
            </div>

            <p>
              Original articles and practical guides will be added as the
              Weathervane analyst library develops.
            </p>
          </div>

          <div className="resources-article-grid">
            {articlePlaceholders.map((article) => (
              <button
                type="button"
                className="resources-article-card"
                key={article.id}
                onClick={() => setComingSoonArticle(article)}
              >
                <div className="resources-article-topline">
                  <ArticleOutlinedIcon aria-hidden="true" />
                  <span>{article.category}</span>
                </div>

                <h3>{article.title}</h3>
                <p>{article.description}</p>

                <span className="resources-card-action">
                  Preview article
                  <ArrowForwardOutlinedIcon aria-hidden="true" />
                </span>
              </button>
            ))}
          </div>
        </section>

        <section
          className="resources-library-section"
          aria-labelledby="external-resources-heading"
        >
          <div className="resources-section-heading">
            <div>
              <span className="resources-section-kicker">
                External Reference Points
              </span>
              <h2 id="external-resources-heading">
                Open-Source Research Resources
              </h2>
            </div>

            <p>
              Review each provider’s terms, privacy practices, licensing,
              access restrictions, and applicable law before use.
            </p>
          </div>

          <div className="resources-category-list">
            {resourceSections.map((section) => {
              const SectionIcon = section.icon;

              return (
                <section
                  className="resources-category"
                  key={section.id}
                  aria-labelledby={`${section.id}-heading`}
                >
                  <div className="resources-category-heading">
                    <div className="resources-category-icon">
                      <SectionIcon aria-hidden="true" />
                    </div>

                    <div>
                      <h3 id={`${section.id}-heading`}>{section.title}</h3>
                      <p>{section.description}</p>
                    </div>
                  </div>

                  <div className="resources-card-grid">
                    {section.resources.map((resource) => (
                      <button
                        type="button"
                        className="resource-card"
                        key={resource.name}
                        onClick={() => setExternalResource(resource)}
                      >
                        <div className="resource-card-topline">
                          <span className="resource-access-badge">
                            {resource.access}
                          </span>

                          <OpenInNewOutlinedIcon
                            className="resource-external-icon"
                            aria-hidden="true"
                          />
                        </div>

                        <div className="resource-card-copy">
                          <h4>{resource.name}</h4>
                          <p>{resource.description}</p>
                        </div>

                        <div className="resource-card-footer">
                          <span>{resource.domain}</span>
                          <span className="resources-card-action">
                            Open resource
                            <ArrowForwardOutlinedIcon aria-hidden="true" />
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
        </section>

        <section className="resources-disclaimer-section">
          <div className="resources-disclaimer-heading">
            <GavelOutlinedIcon aria-hidden="true" />
            <div>
              <span className="resources-section-kicker">
                Third-Party Resource Notice
              </span>
              <h2>Use of External Resources</h2>
            </div>
          </div>

          <div className="resources-disclaimer-grid">
            <article>
              <h3>No ownership or endorsement</h3>
              <p>
                The websites, applications, services, and materials listed on
                this page are operated by independent third parties.
                Weathervane Intel does not own, control, sponsor, certify,
                warrant, approve, or endorse them. Inclusion is not a
                representation that a resource is accurate, secure, lawful,
                suitable, continuously available, or appropriate for a
                particular investigation.
              </p>
            </article>

            <article>
              <h3>User responsibility</h3>
              <p>
                Users are responsible for confirming authorization and
                reviewing applicable laws, organizational policies, licensing,
                terms of service, privacy practices, and access restrictions.
                Analysts should use appropriate documentation,
                operational-security safeguards, and professional judgment.
              </p>
            </article>

            <article>
              <h3>Third-party data and results</h3>
              <p>
                Weathervane Intel does not validate third-party data, search
                results, claims, software, or security practices and is not
                responsible for external content, service changes, outages,
                data handling, losses, or decisions made from third-party
                information.
              </p>
            </article>

            <article>
              <h3>No transfer of investigation data</h3>
              <p>
                This page provides outbound links only. Weathervane Intel does
                not automatically transmit client names, monitored subjects,
                search terms, case information, or investigation data to the
                linked providers.
              </p>
            </article>
          </div>

          <p className="resources-disclaimer-footer">
            External links should be independently evaluated before operational
            use. Report broken, redirected, inappropriate, or concerning links
            to{" "}
            <a href="mailto:info@weathervaneintel.com">
              info@weathervaneintel.com
            </a>
            .
          </p>
        </section>
      </div>

      {externalResource && (
        <div
          className="resources-modal-overlay"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeModals();
          }}
        >
          <section
            className="resources-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="external-resource-modal-title"
          >
            <button
              type="button"
              className="resources-modal-close"
              onClick={closeModals}
              aria-label="Close external resource warning"
            >
              <CloseOutlinedIcon aria-hidden="true" />
            </button>

            <span className="resources-modal-eyebrow">
              External Website
            </span>

            <div className="resources-modal-icon">
              <OpenInNewOutlinedIcon aria-hidden="true" />
            </div>

            <h2 id="external-resource-modal-title">
              You are leaving Weathervane Intel
            </h2>

            <p>
              You are about to open <strong>{externalResource.name}</strong>,
              an independent third-party website.
            </p>

            <div className="resources-modal-destination">
              {externalResource.domain}
            </div>

            <div className="resources-modal-notice">
              <p>
                Weathervane Intel does not own, operate, control, recommend,
                certify, or endorse this resource. Its content, availability,
                privacy practices, security, pricing, and terms are controlled
                by the external provider.
              </p>

              <p>
                Confirm that your intended use is authorized and consistent
                with applicable law, policy, and the provider’s terms before
                proceeding.
              </p>
            </div>

            <div className="resources-modal-actions">
              <button
                type="button"
                className="resources-modal-secondary"
                onClick={closeModals}
              >
                Stay on this page
              </button>

              <button
                type="button"
                className="resources-modal-primary"
                onClick={continueToExternalResource}
              >
                Continue to external site
                <OpenInNewOutlinedIcon aria-hidden="true" />
              </button>
            </div>
          </section>
        </div>
      )}

      {comingSoonArticle && (
        <div
          className="resources-modal-overlay"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeModals();
          }}
        >
          <section
            className="resources-modal resources-coming-soon-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="coming-soon-modal-title"
          >
            <button
              type="button"
              className="resources-modal-close"
              onClick={closeModals}
              aria-label="Close article notice"
            >
              <CloseOutlinedIcon aria-hidden="true" />
            </button>

            <span className="resources-modal-eyebrow">
              Analyst Library
            </span>

            <div className="resources-modal-icon">
              <ArticleOutlinedIcon aria-hidden="true" />
            </div>

            <h2 id="coming-soon-modal-title">Article coming soon</h2>

            <p>
              <strong>{comingSoonArticle.title}</strong> is planned for the
              Weathervane Intel analyst library but is not yet available.
            </p>

            <div className="resources-coming-soon-copy">
              New guides will focus on practical OSINT methodology, protective
              intelligence, documentation, and analyst tradecraft.
            </div>

            <div className="resources-modal-actions">
              <button
                type="button"
                className="resources-modal-primary resources-modal-single-action"
                onClick={closeModals}
              >
                Return to resources
              </button>
            </div>
          </section>
        </div>
      )}
    </main>
  );
};
